import { tmdbService, TMDbMovie, GenreWithMovies } from './tmdbApi';
import { UserProfile as UserProfileType } from '@watchlist/shared';
import { firebaseService } from './firebaseService';
import { extractImdbIdFromSearchQuery } from '../utils/imdbSearchUtils';

// Search error tracking for analytics and debugging
export interface SearchError {
  source: 'movies' | 'watchlists' | 'users' | 'people';
  error: Error;
  timestamp: number;
  query: string;
}

// Track recent search errors for debugging
const recentSearchErrors: SearchError[] = [];
const MAX_ERROR_HISTORY = 10;

function trackSearchError(source: SearchError['source'], error: Error, query: string): void {
  recentSearchErrors.unshift({
    source,
    error,
    timestamp: Date.now(),
    query
  });
  // Keep only recent errors
  if (recentSearchErrors.length > MAX_ERROR_HISTORY) {
    recentSearchErrors.pop();
  }
  console.warn(`🔴 SearchService: ${source} search error tracked:`, error.message);
}

// Retry wrapper for search operations
async function withRetry<T>(
  operation: () => Promise<T>,
  fallbackValue: T,
  options: {
    maxRetries?: number;
    delayMs?: number;
    source?: string;
    query?: string;
  } = {}
): Promise<T> {
  const { maxRetries = 2, delayMs = 500, source = 'unknown', query = '' } = options;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      const isLastAttempt = attempt === maxRetries;

      if (isLastAttempt) {
        console.error(`🔴 SearchService: ${source} failed after ${maxRetries + 1} attempts:`, error);
        if (query && source) {
          trackSearchError(source as SearchError['source'], error as Error, query);
        }
        return fallbackValue;
      }

      console.warn(`⚠️ SearchService: ${source} attempt ${attempt + 1} failed, retrying in ${delayMs}ms...`);
      await new Promise(resolve => setTimeout(resolve, delayMs * (attempt + 1)));
    }
  }

  return fallbackValue;
}

// Export function to get recent errors (for debugging/analytics)
export function getRecentSearchErrors(): SearchError[] {
  return [...recentSearchErrors];
}

export interface SearchResults {
  movies: TMDbMovie[];
  watchlists: any[];
  people: any[];
  profiles: UserProfileType[];
}

export type SearchType = 'movies' | 'watchlists' | 'users' | 'people';

export class SearchService {
  /**
   * Performs a comprehensive search across all content types
   */
  static async performSearch(query: string, type: SearchType): Promise<SearchResults> {
    if (!query.trim() || query.length < 2) {
      return { movies: [], watchlists: [], people: [], profiles: [] };
    }

    const trimmedQuery = query.trim();
    console.log(`🔍 SearchService: Performing ${type} search for "${trimmedQuery}"`);

    const results: SearchResults = {
      movies: [],
      watchlists: [],
      people: [],
      profiles: []
    };

    try {
      switch (type) {
        case 'movies':
          results.movies = await this.searchMovies(trimmedQuery);
          break;
        case 'watchlists':
          results.watchlists = await this.searchWatchlists(trimmedQuery);
          break;
        case 'users':
          results.profiles = await this.searchUsers(trimmedQuery);
          break;
        case 'people':
          results.people = await this.searchPeople(trimmedQuery);
          break;
      }
    } catch (error) {
      console.error(`SearchService: Error performing ${type} search:`, error);
    }

    return results;
  }

  /**
   * Search for movies using enhanced TMDb API with pagination
   */
  static async searchMovies(query: string): Promise<TMDbMovie[]> {
    try {
      console.log(`🎬 SearchService: Basic search for "${query}"`);
      const result = await this.searchMoviesWithPagination(query, 1);
      return result.movies;
    } catch (error) {
      console.error('SearchService: Error in basic movie search:', error);
      return [];
    }
  }

  /**
   * Search for movies using TMDb API with pagination support
   * OPTIMIZED: Defaults to basic search for speed, enhanced search available as opt-in
   * Full details are fetched only when user taps on a movie
   *
   * @param query - Search query
   * @param page - Page number (1-indexed)
   * @param useEnhanced - Use enhanced multi-strategy search (default false - basic TMDB search is faster)
   */
  static async searchMoviesWithPagination(
    query: string,
    page: number = 1,
    useEnhanced: boolean = false // Default to basic search for performance
  ): Promise<{ movies: TMDbMovie[]; totalResults: number; hasMore: boolean; enhancedResults?: Array<{ movie: TMDbMovie; matchedFields: string[]; relevanceScore: number }> }> {
    try {
      const trimmed = query.trim();
      const imdbId = extractImdbIdFromSearchQuery(trimmed);

      // IMDb ID detection - always use direct lookup
      if (imdbId) {
        if (page > 1) {
          return { movies: [], totalResults: 1, hasMore: false, enhancedResults: [] };
        }
        console.log(`🎬 SearchService: IMDb ID detected, resolving via TMDB find: ${imdbId}`);
        try {
          const movie = await tmdbService.getMovieById(imdbId);
          const numericId = movie.id != null ? Number(movie.id) : NaN;
          if (Number.isFinite(numericId) && numericId > 0 && this.validateMovieData(movie)) {
            return {
              movies: [movie],
              totalResults: 1,
              hasMore: false,
              enhancedResults: [{ movie, matchedFields: ['imdb_id'], relevanceScore: 10 }]
            };
          }
        } catch (err) {
          console.error('SearchService: IMDb lookup failed:', err);
        }
        return { movies: [], totalResults: 0, hasMore: false, enhancedResults: [] };
      }

      // Choose search strategy based on useEnhanced parameter
      if (useEnhanced) {
        // Firebase `enhancedSearchMovies`: multi-strategy TMDB search (language, person, etc.)
        console.log(`🎬 SearchService: Enhanced search for "${query}" page ${page}`);
        try {
          const enhancedResults = await tmdbService.enhancedSearchMovies(trimmed, page);
          const validEnhancedResults = enhancedResults.Search.filter(result =>
            this.validateMovieData(result.movie)
          );
          const validMovies = validEnhancedResults.map(result => result.movie);
          return {
            movies: validMovies,
            totalResults: parseInt(enhancedResults.totalResults, 10) || validMovies.length,
            hasMore: (enhancedResults as any).hasMore || false,
            enhancedResults: validEnhancedResults
          };
        } catch (enhancedError) {
          console.warn('🎬 Enhanced search failed, falling back to basic TMDB:', enhancedError);
        }
      }

      // Basic search: fast TMDB search endpoint (default)
      console.log(`🎬 SearchService: Basic TMDB search for "${query}" page ${page}`);
      const basicResults = await tmdbService.searchMovies(trimmed, page);

      // Validate movies
      const validMovies = basicResults.Search
        .filter(movie => this.validateMovieData(movie));

      console.log(`🎬 SearchService: Basic search returned ${validMovies.length} valid movies`);

      // No enrichment needed for basic search - already fast
      return {
        movies: validMovies,
        totalResults: parseInt(basicResults.totalResults) || validMovies.length,
        hasMore: page < Math.ceil((parseInt(basicResults.totalResults) || 0) / 20) // TMDB returns 20 per page
      };
    } catch (error) {
      console.error('SearchService: Error in movie search:', error);

      // Fallback to basic search on error
      try {
        console.log('🔄 SearchService: Falling back to basic TMDB search');
        const basicResults = await tmdbService.searchMovies(query.trim(), page);
        const movies = basicResults.Search
          .filter(movie => this.validateMovieData(movie));

        return {
          movies: movies,
          totalResults: parseInt(basicResults.totalResults) || movies.length,
          hasMore: page < Math.ceil((parseInt(basicResults.totalResults) || 0) / 20)
        };
      } catch (fallbackError) {
        console.error('SearchService: Fallback search also failed:', fallbackError);
        return { movies: [], totalResults: 0, hasMore: false };
      }
    }
  }


  /**
   * Validate movie data for StandardMovieCard compatibility
   */
  private static validateMovieData(movie: TMDbMovie): boolean {
    return !!(
      movie &&
      (movie.title || movie.Title) &&
      (movie.id || movie.imdbID) &&
      movie.title !== 'undefined' &&
      movie.title !== '' &&
      movie.Title !== 'undefined' &&
      movie.Title !== ''
    );
  }

  /**
   * Search for watchlists (basic search - will be enhanced with context in components)
   */
  static async searchWatchlists(query: string): Promise<any[]> {
    try {
      console.log(`📋 SearchService: Basic watchlist search for "${query}"`);

      // Basic implementation - components will override this with their context
      console.log('📋 SearchService: Watchlist search requires component context');
      return [];
    } catch (error) {
      console.error('SearchService: Error searching watchlists:', error);
      return [];
    }
  }

  /**
   * Search for watchlists with context data (for use in components)
   * Enhanced to search by: name, description, creator username, and movie titles
   */
  static async searchWatchlistsWithContext(
    query: string,
    searchWatchlists: (query: string) => any[],
    allWatchlists: any[]
  ): Promise<any[]> {
    try {
      console.log(`📋 SearchService: Enhanced watchlist search for "${query}"`);
      const queryLower = query.toLowerCase().trim();
      // Filter out very short/common terms that cause false positives
      const queryTerms = queryLower.split(/\s+/).filter(t => t.length > 2 && !['the', 'and', 'for', 'with', 'from'].includes(t));

      // Search user watchlists using the provided function
      const userSearchResults = searchWatchlists(query);

      // Enhanced search on public watchlists with relevance scoring
      const scoredResults: Array<{ watchlist: any; score: number; matchedFields: string[] }> = [];

      // Minimum relevance score required to include a watchlist
      const MIN_RELEVANCE_SCORE = 3;

      allWatchlists.forEach(watchlist => {
        if (watchlist.isPrivate) return;

        let score = 0;
        const matchedFields: string[] = [];

        // 1. Name matching (highest weight)
        const nameLower = (watchlist.name || '').toLowerCase();
        if (nameLower === queryLower) {
          score += 10; // Exact match
          matchedFields.push('name:exact');
        } else if (nameLower.includes(queryLower)) {
          score += 5; // Contains full query
          matchedFields.push('name');
        } else if (queryTerms.length > 0 && queryTerms.every(term => nameLower.includes(term))) {
          // All significant terms must match for partial name match
          score += 3;
          matchedFields.push('name:partial');
        }

        // 2. Description matching - require full query match
        const descLower = (watchlist.description || watchlist.summary || '').toLowerCase();
        if (descLower.includes(queryLower)) {
          score += 3;
          matchedFields.push('description');
        }

        // 3. Creator name/username matching
        const creatorName = (watchlist.createdByName || '').toLowerCase();
        const creatorUsername = (watchlist.createdByUsername || '').toLowerCase();
        if (creatorName.includes(queryLower) || creatorUsername.includes(queryLower)) {
          score += 2;
          matchedFields.push('creator');
        }

        // 4. Movie titles within watchlist - require full query match, not partial terms
        const movieDetails = watchlist.movieDetails || [];
        const matchingMovies = movieDetails.filter((movie: any) => {
          const movieTitle = (movie.title || movie.name || '').toLowerCase();
          // Only match if the full query is in the title
          return movieTitle.includes(queryLower);
        });
        if (matchingMovies.length > 0) {
          score += Math.min(matchingMovies.length * 2, 4); // Up to 4 points for movie matches
          matchedFields.push(`movies:${matchingMovies.length}`);
        }

        // 5. Tags/categories matching (if available)
        const tags = watchlist.tags || [];
        if (tags.some((tag: string) => tag.toLowerCase().includes(queryLower))) {
          score += 2;
          matchedFields.push('tags');
        }

        // Only add popularity/recency boosts if there's already a meaningful match
        if (score >= MIN_RELEVANCE_SCORE) {
          // 6. Boost by popularity (follower count)
          const followerCount = watchlist.followerCount || watchlist.likesCount || 0;
          score += Math.min(followerCount / 100, 1); // Up to 1 point for popularity

          // 7. Boost by recency (updated recently)
          const updatedAt = watchlist.updatedAt?.toMillis?.() || watchlist.updatedAt || 0;
          const daysSinceUpdate = (Date.now() - updatedAt) / (1000 * 60 * 60 * 24);
          if (daysSinceUpdate < 7) {
            score += 0.5; // Recently updated bonus
          }

          scoredResults.push({ watchlist, score, matchedFields });
        }
      });

      // Sort by score descending
      scoredResults.sort((a, b) => b.score - a.score);

      console.log(`📋 SearchService: Scored ${scoredResults.length} watchlists (min score: ${MIN_RELEVANCE_SCORE}), top matches:`,
        scoredResults.slice(0, 3).map(r => ({
          name: r.watchlist.name,
          score: r.score.toFixed(2),
          matches: r.matchedFields
        }))
      );

      // Combine user search results with scored public results
      const publicResults = scoredResults.map(r => r.watchlist);
      const allWatchlistResults = [...userSearchResults, ...publicResults];

      // Deduplicate by ID
      const uniqueWatchlists = allWatchlistResults.reduce((acc, current) => {
        const existing = acc.find((w: any) => w.id === current.id);
        if (!existing) {
          acc.push(current);
        }
        return acc;
      }, [] as any[]);

      const results = uniqueWatchlists.slice(0, 12);
      console.log(`📋 SearchService: Found ${results.length} relevant watchlists`);
      return results;
    } catch (error) {
      console.error('SearchService: Error searching watchlists:', error);
      return [];
    }
  }

  /**
   * Search for user profiles using Firebase with retry
   */
  static async searchUsers(query: string): Promise<UserProfileType[]> {
    return withRetry(
      async () => {
        console.log(`👤 SearchService: Searching users for "${query}"`);
        const profileResults = await firebaseService.searchUsers(query, 8);
        console.log(`👤 SearchService: Found ${profileResults.length} users`);
        return profileResults;
      },
      [], // fallback to empty array
      { maxRetries: 2, delayMs: 300, source: 'users', query }
    );
  }

  /**
   * Search for people (actors, directors) using TMDb API with retry
   */
  static async searchPeople(query: string): Promise<any[]> {
    // No retries: search is debounced and the user is typing — a single failure
    // is already covered by the next keystroke firing a fresh request.
    return withRetry(
      async () => {
        console.log(`👥 SearchService: Searching people for "${query}"`);
        const peopleSearchResults = await tmdbService.searchPeople(query, 1);
        return peopleSearchResults.results.slice(0, 8);
      },
      [],
      { maxRetries: 0, source: 'people', query }
    );
  }



  /**
   * Validate search query
   */
  static validateSearchQuery(query: string): { isValid: boolean; error?: string } {
    if (!query) {
      return { isValid: false, error: 'Search query is required' };
    }

    if (query.trim().length < 2) {
      return { isValid: false, error: 'Search query must be at least 2 characters' };
    }

    if (query.length > 100) {
      return { isValid: false, error: 'Search query must be less than 100 characters' };
    }

    return { isValid: true };
  }

  /**
   * Get search result count for a specific type
   */
  static getSearchResultCount(results: SearchResults, type: SearchType): number {
    switch (type) {
      case 'movies': return results.movies.length;
      case 'watchlists': return results.watchlists.length;
      case 'users': return results.profiles.length;
      case 'people': return results.people.length;
      default: return 0;
    }
  }

  /**
   * Clear all search results
   */
  static getEmptySearchResults(): SearchResults {
    return {
      movies: [],
      watchlists: [],
      people: [],
      profiles: []
    };
  }

  /**
   * Get genre categories with top movies and poster URLs
   */
  static async getGenreCategories(): Promise<GenreWithMovies[]> {
    try {
      console.log('🎭 SearchService: Fetching genre categories');
      const genres = await tmdbService.getGenresWithMovies();
      console.log(`🎭 SearchService: Found ${genres.length} genre categories`);
      return genres;
    } catch (error) {
      console.error('SearchService: Error fetching genre categories:', error);
      return [];
    }
  }

  /**
   * Search movies by specific genre ID with optional query filter
   */
  static async searchMoviesByGenre(genreId: number, query?: string, limit: number = 20): Promise<TMDbMovie[]> {
    try {
      console.log(`🎬 SearchService: Searching movies for genre ${genreId}${query ? ` with query "${query}"` : ''}`);

      if (query && query.trim()) {
        // If there's a query, get top movies for the genre and filter by query
        const allMovies = await tmdbService.getTopMoviesForGenre(genreId, limit * 2); // Get more to account for filtering
        const filteredResults = allMovies.filter(movie =>
          movie.title.toLowerCase().includes(query.toLowerCase()) ||
          movie.original_title.toLowerCase().includes(query.toLowerCase())
        );
        console.log(`🎬 SearchService: Found ${filteredResults.length} movies for genre ${genreId} matching query "${query}"`);
        return filteredResults.slice(0, limit);
      } else {
        // Otherwise, get top movies for the genre (fallback to original method)
        const movies = await tmdbService.getTopMoviesForGenre(genreId, limit);
        console.log(`🎬 SearchService: Found ${movies.length} movies for genre ${genreId}`);
        return movies;
      }
    } catch (error) {
      console.error('SearchService: Error searching movies by genre:', error);
      return [];
    }
  }

  /**
   * Search movies by specific genre ID with user language preferences
   */
  static async searchMoviesByGenreWithLanguages(
    genreId: number,
    userLanguages: string[] = ['en'],
    query?: string,
    limit: number = 20
  ): Promise<TMDbMovie[]> {
    try {
      console.log(`🎬 SearchService: Searching movies for genre ${genreId} with languages ${userLanguages.join(', ')}${query ? ` and query "${query}"` : ''}`);

      if (query && query.trim()) {
        // If there's a query, get movies with languages first, then filter by query
        const allMovies = await tmdbService.getTopMoviesForGenreWithLanguages(genreId, userLanguages, limit * 2);
        const filteredResults = allMovies.filter(movie =>
          movie.title.toLowerCase().includes(query.toLowerCase()) ||
          movie.original_title.toLowerCase().includes(query.toLowerCase())
        );
        console.log(`🎬 SearchService: Found ${filteredResults.length} movies for genre ${genreId} with languages matching query "${query}"`);
        return filteredResults.slice(0, limit);
      } else {
        // Get movies with language preferences
        const movies = await tmdbService.getTopMoviesForGenreWithLanguages(genreId, userLanguages, limit);
        console.log(`🎬 SearchService: Found ${movies.length} movies for genre ${genreId} with languages ${userLanguages.join(', ')}`);
        return movies;
      }
    } catch (error) {
      console.error('SearchService: Error searching movies by genre with languages:', error);
      // Fallback to the original method
      return this.searchMoviesByGenre(genreId, query, limit);
    }
  }

  /**
   * Get a specific genre by ID with its top movies
   */
  static async getGenreById(genreId: number): Promise<GenreWithMovies | null> {
    try {
      console.log(`🎭 SearchService: Fetching genre ${genreId}`);
      const genres = await tmdbService.getGenres();
      const genre = genres.find(g => g.id === genreId);

      if (!genre) {
        console.warn(`🎭 SearchService: Genre ${genreId} not found`);
        return null;
      }

      const topMovies = await tmdbService.getTopMoviesForGenre(genreId, 40);
      const posterUrls = topMovies
        .filter(movie => movie.poster_path)
        .map(movie => `https://image.tmdb.org/t/p/w500${movie.poster_path}`)
        .slice(0, 3);

      return {
        ...genre,
        topMovies,
        posterUrls,
        movieCount: topMovies.length
      };
    } catch (error) {
      console.error('SearchService: Error fetching genre by ID:', error);
      return null;
    }
  }

  // Fire a cheap request to warm up the Cloud Function before the user finishes
  // typing. Called from bootCoordinator (primary), SearchPage mount, and search focus
  // (fallback on web). Guards prevent redundant concurrent calls — only one warm-up
  // runs per session; if it fails, subsequent callers may retry.
  private static _warmedUp = false;
  private static _warmUpInFlight = false;

  static warmUp(): void {
    if (SearchService._warmedUp || SearchService._warmUpInFlight) return;
    SearchService._warmUpInFlight = true;
    const start = Date.now();
    void SearchService.searchMoviesWithPagination('a', 1)
      .then(() => {
        SearchService._warmedUp = true;
        console.log(`✅ SearchService: warm-up completed in ${Date.now() - start}ms`);
      })
      .catch((err) => {
        console.warn('⚠️ SearchService: warm-up failed (search may be slow on first query):', err?.message ?? err);
      })
      .finally(() => {
        SearchService._warmUpInFlight = false;
      });
  }

}
